import React, { useState, useEffect } from "react";
import { sbGetSetting, sbSetSetting } from "../utils/supabaseClient";
import { useToast } from "./Toast";
import PromptMarkdownImportModal from "./PromptMarkdownImportModal";
import "../styles/CreateTab.css";

const PromptCreateTab = ({ onCreated }) => {
  const { success, error } = useToast();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [scenario, setScenario] = useState("");
  const [content, setContent] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [saving, setSaving] = useState(false);
  const [showMarkdownImport, setShowMarkdownImport] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);
  const [categoryMode, setCategoryMode] = useState("select");

  useEffect(() => {
    loadExistingCategories();
  }, []);

  const loadExistingCategories = async () => {
    try {
      const prompts = await sbGetSetting("origamic-prompts");
      if (Array.isArray(prompts)) {
        const categories = [...new Set(prompts.map(p => p.cat).filter(Boolean))].sort();
        setExistingCategories(categories);
      }
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      error("Title and content are required");
      return;
    }

    setSaving(true);
    try {
      const newPrompt = {
        id: `prompt_${Date.now()}`,
        title: title.trim(),
        cat: category.trim(),
        scenario: scenario.trim(),
        text: content.trim(),
        scheduled: scheduled ? new Date(scheduled).toISOString() : null,
        addedAt: new Date().toISOString(),
        draft: true,
      };

      let prompts = await sbGetSetting("origamic-prompts");
      if (!Array.isArray(prompts)) prompts = [];

      prompts.push(newPrompt);
      await sbSetSetting("origamic-prompts", prompts);

      success("Prompt saved as draft");
      setTitle("");
      setCategory("");
      setScenario("");
      setContent("");
      setScheduled("");

      if (onCreated) {
        onCreated();
      }
    } catch (err) {
      console.error("Error saving prompt:", err);
      error("Failed to save prompt");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkdownImport = async (importedPrompts) => {
    try {
      let prompts = await sbGetSetting("origamic-prompts");
      if (!Array.isArray(prompts)) prompts = [];

      prompts.push(...importedPrompts);
      await sbSetSetting("origamic-prompts", prompts);

      setShowMarkdownImport(false);
      success(`${importedPrompts.length} prompt(s) imported successfully`);
    } catch (err) {
      console.error("Error importing prompts:", err);
      error("Failed to import prompts");
    }
  };

  if (showMarkdownImport) {
    return (
      <PromptMarkdownImportModal
        onImport={handleMarkdownImport}
        onCancel={() => setShowMarkdownImport(false)}
      />
    );
  }

  return (
    <div className="create-container">
      <h2>Create New Prompt</h2>

      <form onSubmit={handleSubmit} className="create-form">
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Marketing Strategy"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Category</label>
          {categoryMode === "select" ? (
            <select
              value={category}
              onChange={(e) => {
                if (e.target.value === "new") {
                  setCategoryMode("add");
                } else {
                  setCategory(e.target.value);
                }
              }}
              disabled={saving}
            >
              <option value="">-- Select Category --</option>
              {existingCategories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value="new">+ Add New Category</option>
            </select>
          ) : (
            <>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Enter new category name..."
                disabled={saving}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setCategoryMode("select")}
                disabled={saving}
                style={{
                  marginTop: "8px",
                  padding: "8px 12px",
                  background: "#7A9CC4",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Back to Categories
              </button>
            </>
          )}
        </div>

        <div className="form-group">
          <label>Scenario</label>
          <textarea
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            placeholder="e.g., Customer onboarding, Troubleshooting, etc."
            disabled={saving}
            rows="3"
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid rgba(59, 130, 246, 0.15)",
              borderRadius: "8px",
              background: "#050e1a",
              color: "white",
              fontFamily: "inherit",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div className="form-group">
          <label>Scheduled Date (Optional)</label>
          <input
            type="date"
            value={scheduled}
            onChange={(e) => setScheduled(e.target.value)}
            disabled={saving}
            title="Schedule when this prompt should be published"
            style={{ width: "100%" }}
          />
          <small style={{ color: "#7A9CC4", marginTop: "4px", display: "block" }}>
            Optional: Schedule this prompt to be published on a future date
          </small>
        </div>

        <div className="form-group">
          <label>Prompt Content * (Markdown)</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter prompt content (supports markdown)..."
            rows="10"
            disabled={saving}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid rgba(59, 130, 246, 0.15)",
              borderRadius: "8px",
              background: "#050e1a",
              color: "white",
              fontFamily: "inherit",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />

          {/* Markdown Formatting Guide */}
          <details style={{ marginTop: "20px", padding: "16px", background: "rgba(59, 130, 246, 0.05)", borderRadius: "8px", border: "1px solid rgba(59, 130, 246, 0.15)" }}>
            <summary style={{ cursor: "pointer", fontWeight: "600", color: "#5B9EF0", fontSize: "14px", userSelect: "none" }}>
              Markdown Formatting Guide
            </summary>
            <div style={{ marginTop: "12px", color: "#9DB4D4", fontSize: "13px", lineHeight: "1.6" }}>
              <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Fence Types (Info Boxes)</h4>
              <ul style={{ marginLeft: "20px" }}>
                <li><code>:::tip</code> - Green info box with 💡 icon</li>
                <li><code>:::warning</code> - Orange warning box with ⚠️ icon</li>
                <li><code>:::error</code> - Red error box with ❌ icon</li>
                <li><code>:::info</code> - Blue info box with ℹ️ icon</li>
                <li><code>:::button Label, URL</code> - Clickable button (orange)</li>
              </ul>

              <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Basic Formatting</h4>
              <ul style={{ marginLeft: "20px" }}>
                <li><code>**text**</code> - Bold</li>
                <li><code>*text*</code> - Italic</li>
                <li><code>`code`</code> - Inline code</li>
                <li><code># Heading</code> - Large heading</li>
                <li><code>- [ ]</code> - Checkbox (read-only)</li>
                <li><code>[text](url)</code> - Link</li>
              </ul>

              <h4 style={{ color: "#5B9EF0", marginTop: "12px", marginBottom: "8px" }}>Examples</h4>
              <div style={{ background: "#050e1a", padding: "12px", borderRadius: "6px", overflow: "auto", marginTop: "8px", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                <pre style={{ margin: "0", color: "#5B9EF0", fontSize: "12px" }}>
{`:::tip
This is a helpful tip
:::

:::button Get Started, https://example.com
:::

**Bold text** and *italic text*

- Bullet 1
- Bullet 2`}
                </pre>
              </div>

              <p style={{ marginTop: "12px", color: "#7A9CC4", fontSize: "12px" }}>
                📥 Download the full guide: <a href="/MARKDOWN_FORMATTING_GUIDE.md" style={{ color: "#5B9EF0", textDecoration: "underline" }}>MARKDOWN_FORMATTING_GUIDE.md</a>
              </p>
            </div>
          </details>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save Prompt"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowMarkdownImport(true)}
            disabled={saving}
            style={{ background: "#6366F1", color: "white" }}
          >
            📝 Import from Markdown
          </button>
        </div>
      </form>
    </div>
  );
};

export default PromptCreateTab;
